import type { ReactElement, ReactNode, ElementType } from 'react'
import { useRef, useState, FocusEvent, KeyboardEvent, useEffect } from 'react'
import {
  ClickAwayListener,
  Paper,
  TextField,
  InputBase,
  List,
  IconButton,
  Divider,
  Dialog,
  AppBar,
  Toolbar,
  Box,
  Grow,
  InputAdornment,
  CircularProgress,
  ListSubheader,
  ListItem,
  TextFieldProps,
  useMediaQuery,
  useAutocomplete,
  UseAutocompleteProps,
  AutocompleteFreeSoloValueMapping,
} from '@mui/material'

import { ExpandMore, ChevronLeft, Close } from '@dhl/icons'
import { breakpoints } from '@dhl/theme'
import { cast, isNotEmpty } from '@dhl/utils'

type RootProps = {
  children: ReactElement
  onClickAway: (event: MouseEvent | TouchEvent) => void
  isDesktop: boolean
}

const bodyStyles = {
  [breakpoints.up('sm')]: {
    overflowY: 'auto',
  },
  [breakpoints.down('sm')]: {
    marginTop: '48px',
  },
}

const menuItemStyles = {
  '&[data-focus="true"]': {
    background: 'rgba(0, 0, 0, 0.08)',
  },
}

const Root = ({ onClickAway, isDesktop, children }: RootProps) =>
  isDesktop ? <ClickAwayListener onClickAway={onClickAway}>{children}</ClickAwayListener> : children

type ContainerProps = {
  isOpen: boolean
  autoFocus: boolean
  children: ReactNode
  isDesktop: boolean
}

const Container = ({ isOpen, autoFocus, children, isDesktop }: ContainerProps) =>
  isDesktop ? (
    <Grow in={isOpen}>
      <Paper
        sx={({ spacing }) => ({
          position: 'absolute',
          zIndex: 1300,
          right: spacing(-0.5),
          left: spacing(-0.5),
          top: spacing(-0.5),
          marginBottom: spacing(1),
          minHeight: spacing(8),
          visibility: isOpen ? 'visible' : 'hidden',
        })}
      >
        {isOpen && children}
      </Paper>
    </Grow>
  ) : (
    <Dialog open={isOpen} disablePortal fullScreen transitionDuration={autoFocus ? 0 : undefined}>
      {children}
    </Dialog>
  )

type BodyProps = {
  children: ReactNode
  maxHeight: string
  isDesktop: boolean
}

const Body = ({ maxHeight, children, isDesktop }: BodyProps) =>
  isDesktop ? (
    <>
      <Divider />

      <Box
        data-test="body"
        sx={{
          ...bodyStyles,
          maxHeight,
        }}
      >
        {children}
      </Box>
    </>
  ) : (
    <Box data-test="body" sx={bodyStyles}>
      {children}
    </Box>
  )

type GroupProps = {
  label?: string
  children: ReactNode
  isDesktop: boolean
}

const Group = ({ label, children, isDesktop }: GroupProps) =>
  isDesktop ? (
    <>
      {label && (
        <ListSubheader disableSticky component="div" data-test="sub-header" sx={{ pl: 2 }}>
          {label}
        </ListSubheader>
      )}
      {children}
    </>
  ) : (
    <>
      {label && (
        <ListSubheader disableSticky component="div" data-test="sub-header" sx={{ pl: 2 }}>
          {label}
        </ListSubheader>
      )}
      <Box m={1} mt={label ? 0 : 2} data-test="body">
        <Paper>{children}</Paper>
      </Box>
    </>
  )

export type AutocompleteProps<T, FreeSolo extends boolean | undefined> = Omit<
  UseAutocompleteProps<T, false, false, FreeSolo>,
  'multiple' | 'onOpen' | 'onClose' | 'open'
> &
  Pick<
    TextFieldProps,
    'name' | 'label' | 'error' | 'helperText' | 'fullWidth' | 'required' | 'margin' | 'placeholder' | 'size'
  > & {
    getOptionLabel: (option: T | AutocompleteFreeSoloValueMapping<FreeSolo>) => string
    className?: string
    renderOption?: (option: T) => ReactNode
    icon?: ElementType
    isLoading?: boolean
    autoFocus?: boolean
    autoHighlight?: boolean
    selectOnFocus?: boolean
    clearable?: boolean
    disabled?: boolean
    maxHeight?: string
    onClose?: () => void
  }

export function Autocomplete<T, FreeSolo extends boolean | undefined>({
  label,
  error,
  helperText,
  fullWidth = false,
  required = false,
  value,
  id,
  getOptionLabel,
  margin,
  placeholder,
  name,
  className,
  freeSolo,
  onClose,
  autoFocus = false,
  selectOnFocus = true,
  isLoading = false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  icon: Icon = ExpandMore,
  maxHeight = '300px',
  clearable = false,
  disabled = false,
  size = 'medium',
  ...props
}: AutocompleteProps<T, FreeSolo>): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))

  const [isOpen, setIsOpen] = useState<boolean>(autoFocus)
  const [isAnimating, setAnimating] = useState<boolean>(false)

  useEffect(() => {
    if (!isOpen) {
      onClose && onClose()
    }

    const timer = setTimeout(() => setAnimating(false), 10)

    return () => clearTimeout(timer)
  }, [isOpen, onClose, isAnimating])

  const toggleOverlay = (newValue: boolean) => {
    setAnimating(true)
    setIsOpen(newValue)
  }

  return (
    <Root
      isDesktop={isDesktop}
      onClickAway={(event) => {
        if (wrapperRef && !wrapperRef.current?.contains?.(event.target as Node)) {
          toggleOverlay(false)
        }
      }}
    >
      <Box className={className} data-test={`${id}-autocomplete`} position="relative">
        <TextField
          inputRef={inputRef}
          ref={wrapperRef}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          fullWidth={fullWidth}
          required={required}
          autoFocus={autoFocus}
          disabled={!isOpen && !isAnimating ? disabled : true}
          margin={margin}
          size={size}
          name={name}
          onClick={() => toggleOverlay(!disabled)}
          onFocus={() => {
            if (!isAnimating) {
              toggleOverlay(!disabled)
            }
          }}
          // eslint-disable-next-line no-nested-ternary
          value={!value ? '' : freeSolo ? value : getOptionLabel(value)}
          variant="outlined"
          autoComplete={`nope-${new Date().getTime()}`}
          slotProps={{
            input: {
              endAdornment: isLoading ? (
                <InputAdornment position="end">
                  <Box display="flex" mr={1}>
                    <CircularProgress size={24} />
                  </Box>
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <>
                    {clearable && !!value && (
                      <IconButton
                        data-test="clear-button"
                        disabled={disabled}
                        size={margin === 'dense' ? 'small' : 'medium'}
                        tabIndex={-1}
                        onClick={(e) => {
                          // @ts-ignore T can be string | null
                          props.onChange(null)
                          e.stopPropagation()
                          e.preventDefault()
                        }}
                      >
                        <Close />
                      </IconButton>
                    )}
                    {props.options.length > 0 && (
                      <IconButton tabIndex={-1} disabled={disabled} size={margin === 'dense' ? 'small' : 'medium'}>
                        <Icon />
                      </IconButton>
                    )}
                  </>
                </InputAdornment>
              ),
            },

            inputLabel: { shrink: isNotEmpty(value), focused: false },
          }}
        />

        <Container isOpen={isOpen} autoFocus={autoFocus} isDesktop={isDesktop}>
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <InnerAutocomplete<T, FreeSolo>
            value={value}
            id={id}
            isDesktop={isDesktop}
            getOptionLabel={getOptionLabel}
            toggleOverlay={toggleOverlay}
            maxHeight={maxHeight}
            freeSolo={freeSolo}
            selectOnFocus={selectOnFocus}
            // autoSelect needs to be true to update the value in freeSolo mode
            autoSelect={freeSolo ? true : props?.autoSelect || false}
            clearOnBlur={props?.clearOnBlur || false}
            {...props}
          />
        </Container>
      </Box>
    </Root>
  )
}

type InnerProps<T, FreeSolo extends boolean | undefined> = UseAutocompleteProps<T, false, false, FreeSolo> & {
  getOptionLabel: (option: T) => string
  toggleOverlay: (isOpen: boolean) => void
  renderOption?: (option: T) => ReactNode
  maxHeight: string
  isDesktop: boolean
}

function InnerAutocomplete<T, FreeSolo extends boolean | undefined>({
  toggleOverlay,
  value,
  options,
  id: idProp,
  getOptionLabel,
  isOptionEqualToValue,
  groupBy,
  renderOption,
  maxHeight,
  isDesktop,
  selectOnFocus = true,
  autoHighlight = true,
  ...props
}: InnerProps<T, FreeSolo>) {
  const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
    multiple: false,
    open: true,
    value,
    options,
    getOptionLabel,
    isOptionEqualToValue,
    ...(groupBy ? { groupBy } : {}),
    id: idProp,
    autoHighlight,
    selectOnFocus,
    ...props,
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { ref, ...inputProps }: any = getInputProps()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rootProps: any = getRootProps()

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Tab':
        ref.current.blur()
        toggleOverlay(false)
        break
      case 'Enter':
        rootProps.onKeyDown(e)
        e.preventDefault()
        e.stopPropagation()
        ref.current.blur()
        toggleOverlay(false)
        break
      default:
        rootProps.onKeyDown(e)
    }
  }

  useEffect(() => {
    // Fix issue where the input is pushed out of the viewport when the keyboard is opened on a device
    setTimeout(() => {
      window.scrollTo({ top: 0 })

      // Focus and ecplicitly select the input text. Fixes a bug on iPad where focus was lost.
      // Dont do this in Cypress tests as it causes the tests to fail.
      // See CON-3858
      if (!window.Cypress) {
        ref.current?.focus()
        requestAnimationFrame(() => {
          ref.current?.select()
        })
      }
    }, 200)
  }, [ref])

  type OptionsGroup = { key: string; options: T[]; index: number; group: string }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div {...rootProps} onClick={() => {}} onKeyDown={onKeyDown}>
      {!isDesktop && (
        <AppBar
          position="fixed"
          sx={{
            background: (theme) => theme.palette.grey[100],
          }}
        >
          <Toolbar>
            <IconButton onClick={() => toggleOverlay(false)} size="large">
              <ChevronLeft />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <InputBase
        {...inputProps}
        autoFocus
        fullWidth
        inputRef={ref}
        data-test="search"
        autoComplete={`nope-${new Date().getTime()}`}
        inputProps={{
          onFocus: (e: FocusEvent<HTMLInputElement>) => {
            if (selectOnFocus) {
              setTimeout(() => {
                e.target.select()
              })
            }
          },
        }}
        sx={{
          input: ({ spacing }) => ({
            height: spacing(6),
            textIndent: spacing(2),
            padding: 0,
            [breakpoints.down('sm')]: {
              zIndex: 9999,
              position: 'fixed',
              top: 0,
              left: '56px',
              height: '56px',
            },
          }),
        }}
      />
      {groupedOptions.length > 0 && (
        <Body maxHeight={maxHeight} isDesktop={isDesktop}>
          {groupBy && (
            <List {...getListboxProps()} onClick={() => toggleOverlay(false)}>
              {/* eslint-disable-next-line @typescript-eslint/no-shadow */}
              {cast<OptionsGroup[]>(groupedOptions).map(({ key, options, index, group }) => (
                <li key={key}>
                  <Group key={key} label={group} isDesktop={isDesktop}>
                    <List>
                      {options.map((option: T, innerIndex: number) => (
                        <ListItem
                          {...getOptionProps({ option, index: index + innerIndex })}
                          // eslint-disable-next-line react/no-array-index-key
                          key={index + innerIndex}
                          data-test="option"
                          sx={menuItemStyles}
                        >
                          {renderOption ? renderOption(option) : getOptionLabel(option)}
                        </ListItem>
                      ))}
                    </List>
                  </Group>
                </li>
              ))}
            </List>
          )}

          {!groupBy && (
            <Group isDesktop={isDesktop}>
              <List {...getListboxProps()} onClick={() => toggleOverlay(false)}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {groupedOptions.map((option: any, index) => (
                  <ListItem
                    {...getOptionProps({ option, index })}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${index}-${getOptionLabel(option)}`}
                    data-test="option"
                    sx={menuItemStyles}
                  >
                    {renderOption ? renderOption(option) : getOptionLabel(option)}
                  </ListItem>
                ))}
              </List>
            </Group>
          )}
        </Body>
      )}
    </div>
  )
}
